import * as _ from 'lodash'
import valueFilters from '../valueFilters';
import validator from '../validator';
import modelFactory, { IModel } from '../modelFactory';
import model from './model';
import changeTrackable, { IChangeTrackable } from './changeTrackable';
import enums from '../enum';
import { definition, modelRules } from './jobCostLineItemModelExtensions';

const TYPES = [
	'builder-upgrade',
	'cabinet',
	'client-upgrade',
	'delivery',
	'glass',
	'installation',
	'installation-adjustment',
	'installation-custom',
	'labour',
	'misc',
	'outside-supplier',
	'referral-fee',
	'rubbish-removal',
	'service',
	'solid-surface-counter',
	'special-order-glass',
	'subtrade',
	'tax',
	'travel',
	'warehouse',
	'warranty',
	'calculated-fee'
] as const;

export type LineItemType = typeof TYPES[number];

const getContractReportingFlagsMask = () => enums.jobCostLineItemReportingFlags.Contract_Hidden
	| enums.jobCostLineItemReportingFlags.Contract_With_Price
	| enums.jobCostLineItemReportingFlags.Contract_Without_Price;

export interface IJobCostLineItem extends IModel, IChangeTrackable {
	contractorKey?: string;
	contractVisibility: number;
	cost: number | string;
	costFactor?: number;
	description?: string;
	freight: number;
	itemNote?: string;
	doorSizeKey?: string;
	glassTypeKey?: string;
	group?: string;
	height?: number;
	installSheetKey?: string;
	isCabinetCount?: boolean;
	isPackaged?: boolean;
	isPreSubtotal: boolean;
	isAutoCalculated?: boolean;
	isChargedToBuilder?: boolean;
	isOptional: boolean;
	isSystemManaged: boolean;
	listPrice?: number;
	margin?: number;
	orderKey?: string;
	orderNote?: string;
	pieceCount?: number;
	productKey?: string;
	productName?: string;
	quantity: number;
	referenceNumber?: string;
	relatedType?: string;
	reportingFlags: number;
	sellPrice?: number;
	sortIndex: number;
	subContracted?: boolean;
	subTradeTypeKey?: string;
	subType?: string;
	totalCost: number;
	type?: LineItemType;
	unit?: string;
	vendorKey?: string;
	width?: number;
	workAreaKey?: string;
	duplicate(): IJobCostLineItem
	getNetValues(zeroOptional?: boolean, ignoreDeleted?: boolean): { cost: number, freight; number, sell: number };
}

const jobCostLineItemFactory = Object.assign(modelFactory.createFactory<IJobCostLineItem>('JobCostLineItem', _.assign({}, model, changeTrackable, {
	contractorKey: {
		setFilters: valueFilters.clean
	},
	contractVisibility: {
		nonSerialized: true
	},
	_set_contractVisibility: function (val) {
		const flag = Number(val);
		if (val && !isNaN(flag)) {
			const cleanFlags = enums.jobCostLineItemReportingFlags.unset(getContractReportingFlagsMask(), this.reportingFlags);

			this.reportingFlags = enums.jobCostLineItemReportingFlags.set((flag & getContractReportingFlagsMask()), cleanFlags);
		}
	},
	_get_contractVisibility: function () {
		return this.reportingFlags & getContractReportingFlagsMask();
	},
	cost: {
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		defaultValue: 0,
		rules: [
			(value, target) => {
				if (['solid-surface-counter'].indexOf(target.type) !== -1) {
					return (value !== 0) || 'Cost cannot be zero.';
				}
				else if (['special-order-glass'].indexOf(target.type) !== -1) {
					return (value !== 0) || 'Special glass cost cannot be zero.';
				}
				else if (['referral-fee'].indexOf(target.type) !== -1) {
					return value > 0 || 'An amount is required.';
				}
			}
		]
	},
	costFactor: {
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		rules: [
			(value, target) => {
				if (['cabinet'].indexOf(target.type) !== -1) {
					return (value > 0) || 'Cost Factor must be greater than zero.';
				}
			}
		]
	},
	description: {
		rules: [
			(value, target) => {
				if (['misc'].indexOf(target.type) !== -1) {
					return validator.hasValue(value) || 'A description is required.';
				}
				else if (['referral-fee'].indexOf(target.type) !== -1) {
					return validator.hasValue(value) || 'A recipient is required.';
				}
			}
		]
	},
	freight: {
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		defaultValue: 0
	},
	itemNote: {
		rules: [
			(value, target) => {
				if (['special-order-glass'].indexOf(target.type) !== -1) {
					return validator.hasValue(value) || 'Special glass type is required.';
				}
			}
		]
	},
	doorSizeKey: {
		setFilters: valueFilters.clean,
		rules: [
			(value, target) => {
				if (['glass'].indexOf(target.type) !== -1) {
					return validator.hasValue(value) || 'Door size is required.';
				}
			}
		]
	},
	glassTypeKey: {
		setFilters: valueFilters.clean,
		rules: [
			(value, target) => {
				if (['glass'].indexOf(target.type) !== -1) {
					return validator.hasValue(value) || 'Glass Type is required.';
				}
			}
		]
	},
	group: {
		setFilters: valueFilters.toString
	},
	height: {
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		rules: [
			(value, target) => {
				if (['special-order-glass'].indexOf(target.type) !== -1) {
					return validator.hasValue(value) || 'Special glass height is required.';
				}
			}
		]
	},
	installSheetKey: {
		setFilters: valueFilters.clean
	},
	isCabinetCount: {
		setFilters: valueFilters.toBoolean
	},
	isPackaged: {
		setFilters: valueFilters.toBoolean
	},
	isPreSubtotal: {
		nonSerialized: true
	},
	_set_isPreSubtotal: function (val) {
		if (val) {
			this.reportingFlags = enums.jobCostLineItemReportingFlags.set(enums.jobCostLineItemReportingFlags.Pre_Subtotal, this.reportingFlags);
		}
		else {
			this.reportingFlags = enums.jobCostLineItemReportingFlags.unset(enums.jobCostLineItemReportingFlags.Pre_Subtotal, this.reportingFlags);
		}
	},
	_get_isPreSubtotal: function () {
		return !!(this.reportingFlags & enums.jobCostLineItemReportingFlags.Pre_Subtotal);
	},
	isAutoCalculated: {
		setFilters: valueFilters.toBoolean
	},
	isChargedToBuilder: {
		setFilters: valueFilters.toBoolean
	},
	isOptional: {
		setFilters: [valueFilters.toBoolean],
		defaultValue: false
	},
	isSystemManaged: {
		setFilters: [valueFilters.toBoolean],
		defaultValue: false
	},
	listPrice: {
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber
	},
	margin: {
		setFilters: [valueFilters.cleanNumber]
	},
	orderKey: {
		setFilters: valueFilters.clean
	},
	orderNote: {
		rules: [
			(value, target) => {
				if (['special-order-glass'].indexOf(target.type) !== -1) {
					return validator.hasValue(value) || 'Special glass supplier is required.';
				}
			}
		]
	},
	pieceCount: {
		setFilters: valueFilters.cleanInteger,
		getFilters: valueFilters.toNumber
	},
	productKey: {
		setFilters: valueFilters.clean
	},
	productName: {
		setFilters: valueFilters.toString,
		rules: [
			(value, target) => {
				if (['outside-supplier', 'warehouse'].indexOf(target.type) !== -1) {
					return validator.hasValue(value) || 'Item is required.';
				}
			}
		]
	},
	quantity: {
		setFilters: function (val) { // non-arrow function allows this to be bound to the model instance
			if (['installation', 'installation-custom', 'service'].indexOf(this.type) !== -1) {
				return valueFilters.cleanNumber(val);
			}
			else {
				return valueFilters.cleanInteger(val);
			}
		},
		getFilters: valueFilters.toNumber,
		defaultValue: 1,
		rules: [(value, target) => {
			if (['installation', 'installation-custom', 'service'].indexOf(target.type) !== -1) {
				return validator.isNumber(value, target);
			}
			else {
				return validator.isInteger(value, target);
			}
		},
		(value, target) => {
			if (['installation', 'installation-custom', 'service'].indexOf(target.type) !== -1) {
				return validator.greaterThan(0, 'Quantity must be greater than zero')(value, target, 'quantity');
			}
		}
		]
	},
	referenceNumber: {
		setFilters: valueFilters.toString
	},
	relatedType: { // TODO: validate this properly
		setFilters: valueFilters.clean
	},
	reportingFlags: {
		setFilters: valueFilters.toNumber,
		defaultValue: () => enums.jobCostLineItemReportingFlags.Contract_Hidden
	},
	sellPrice: {
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		rules: [
			(value, target) => {
				if (['glass', 'special-order-glass'].indexOf(target.type) !== -1) {
					return validator.hasValue(value) || 'Sell price is required.';
				}
			}
		]
	},
	sortIndex: {
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		rules: validator.number(),
		defaultValue: 0.0
	},
	subContracted: {
		setFilters: valueFilters.toBoolean
	},
	subTradeTypeKey: {
		setFilters: valueFilters.clean
	},
	subType: {
		setFilters: valueFilters.clean
	},
	totalCost: {
		writable: false,
		nonSerialized: true
	},
	_get_totalCost: function () {
		if (['cabinet', 'installation', 'service', 'subtrade', 'tax', 'warranty'].indexOf(this.type) === -1) {
			// for everything but cabinet, subtrade, installation, service, tax, and warranty, the costFactor influences the total cost
			return Number(((this.cost + (this.cost * (this.costFactor || 0))) + (this.freight || 0)) * this.quantity);
		}
		return Number((this.cost + (this.freight || 0)) * this.quantity);
	},
	type: {
		setFilters: valueFilters.clean,
		rules: validator.inList(TYPES)
	},
	unit: {
		setFilters: valueFilters.clean
	},
	vendorKey: {
		setFilters: valueFilters.clean,
		rules: [
			(value, target) => {
				if (['solid-surface-counter', 'outside-supplier'].indexOf(target.type) !== -1 || (target.schemaVersion > 0 && target.type === 'cabinet')) {
					return validator.hasValue(value) || 'Vendor is required.';
				}
			}
		]
	},
	width: {
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		rules: [
			(value, target) => {
				if (['special-order-glass'].indexOf(target.type) !== -1) {
					return validator.hasValue(value) || 'Special glass width type is required.';
				}
			}
		]
	},
	workAreaKey: {
		setFilters: valueFilters.clean
	},
	duplicate: function () {
		return jobCostLineItemFactory(_.omit(this.toDocument(), 'key', 'rootKey', 'changeOrderKey'));
	},
	getNetValues: function (zeroOptional, ignoreDeleted) {
		const result = {
			cost: zeroOptional && this.isOptional ? 0 : this.totalCost || 0,
			freight: zeroOptional && this.isOptional ? 0 : this.freight || 0,
			sell: zeroOptional && this.isOptional ? 0 : this.sellPrice || 0
		};

		if (this.isModification && this.parent) {
			if (this.isOptional && !this.parent.isOptional) {
				result.cost = -this.parent.totalCost;
				result.freight = -this.parent.freight;
				result.sell = -this.parent.sellPrice;
			}
			else if (!this.isOptional && this.parent.isOptional) {
				// this does nothing, the cost counts
			}
			else if (this.totalCost === this.parent.totalCost) {
				result.cost = result.freight = result.sell = 0;
			}
			else {
				result.cost = (this.totalCost - this.parent.totalCost);
				result.freight = (this.freight - this.parent.freight);
				result.sell = (this.sellPrice - this.parent.sellPrice);
			}
		}
		else if (!ignoreDeleted && this.isDeletion && this.parent) {
			result.cost = -this.parent.totalCost;
			result.freight = -this.parent.freight;
			result.sell = -this.parent.sellPrice;
		}

		return result;
	}
}, definition || {})), { TYPES }, modelRules || []);

export default jobCostLineItemFactory;